import './App.css';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import ReactECharts from 'echarts-for-react';
import { Table } from 'antd';
import unit from './unit';
import { message } from 'antd';

const StockChart = (props) => { 
    let code = props.code;
    let type = props.type;
    let renderTitleOnly = props.renderTitleOnly;
    let renderChartAndTable = props.renderChartAndTable;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stockData, setStockData] = useState({});
    const [stockInfo, setStockInfo] = useState(null);
    const [isFollow, setIsFollow] = useState(0);
    // 新增状态：判断图表和表格是否有数据
    const [hasChartData, setHasChartData] = useState(false);
    const [hasTableData, setHasTableData] = useState(false);
    const chartRef = useRef(null);
    const childRef = useRef(null);
    const [messageApi, contextHolder] = message.useMessage();

    // 添加日期格式化辅助函数
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.length === 8) {
            return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6)}`;
        }
        return dateStr;
    };

    // 使用 useCallback 包装 fetchData
    const fetchData = useCallback(async () => {
        try {
            const path = '/pc/Gudong/getStockCharts';
            const data = await new Promise((resolve, reject) => {
                unit.httpGet(path, { "type": type, "code": code }, (responseData) => {
                    if (responseData) {
                        resolve(responseData);
                    } else {
                        reject(new Error('暂无数据'));
                    }
                });
            });

            // 判断列表数据是否存在
            const hasData = data.list && data.list.length > 0;
            
            const _stockData = {
                quarters: hasData ? data.list.map(item => formatDate(item.day)) : [],
                percent: hasData ? data.list.map(item => item.percent) : [],
                holder_num: hasData ? data.list.map(item => item.holder_num) : [],
                change_percent: hasData ? data.list.map(item => item.change_percent) : [],
                price: hasData ? data.list.map(item => item.price) : [],
            };

            setStockData(_stockData);
            setStockInfo(data.info);
            setIsFollow(data.info?.['is_follow'] || 0);
            
            // 设置数据存在状态
            setHasChartData(hasData);
            setHasTableData(hasData);
            
            setLoading(false);
            setError(null);
            
            // 更新页面标题和描述
            updatePageMeta(data.info);
        } catch (err) {
            console.error('获取数据失败:', err.message);
            setError(`获取数据失败: ${err.message}`);
            setHasChartData(false);
            setHasTableData(false);
            setLoading(false);
        }
    }, [code, type]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // 更新页面标题和meta描述
    const updatePageMeta = (info) => {
        if (!info) return;
        
        const { stock_name, stock_code } = info;
        
        // 设置页面标题
        document.title = `${stock_name}(${stock_code})股东数据 - 仓位在线`;
        
        // 设置meta描述
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
            metaDescription.setAttribute('content', 
                `查看${stock_name}(${stock_code})最新股东数据，包括持仓比例、持股数量、` +
                `持仓变化等信息，全面了解机构和牛散的持仓动向。`
            );
        }
    };

    // 使用 useCallback 包装 getProcessedData
    const getProcessedData = useCallback(() => {
        if (!stockData || Object.keys(stockData).length === 0 || !hasChartData) return null;

        return {
            quarters: stockData.quarters,
            percent: stockData.percent.map(percent => parseFloat(percent).toFixed(2)),
            price: stockData.price.map(price => parseFloat(price).toFixed(2)),
            holder_num: stockData.holder_num.map(holder_num => unit.bigNumberTransform(holder_num)),
            change_percent: stockData.change_percent.map(change_percent => parseFloat(change_percent).toFixed(2)),
        };
    }, [stockData, hasChartData]);

    // 使用 useCallback 包装 generateChartOption
    const generateChartOption = useCallback(() => {
        const processedData = getProcessedData();
        if (!processedData) return {};
        
        // 计算股价的最大值和最小值
        const priceMax = Math.max(...processedData.price.map(Number));
        const priceMin = Math.min(...processedData.price.map(Number));

        return {
            tooltip: {
                trigger: 'axis',
                axisPointer: {
                    type: 'cross',
                    crossStyle: {
                        color: '#999'
                    }
                },
                formatter: function (params) {
                    let result = `${params[0].axisValue}<br/>`;
                    params.forEach(param => {
                        result += `<span style="display:inline-block;margin-right:5px;border-radius:10px;width:10px;height:10px;background-color:${param.color}">
                        </span> ${param.seriesName}: ${param.value}${param.seriesName === '持仓占比' ? '%' : '元'}<br/>`;
                    });
                    return result;
                }
            },
            legend: {
                left: 'left',
                data: ['持仓占比', '股价(元)'],
                selectedMode: 'multiple'
            },
            xAxis: {
                type: 'category',
                data: processedData.quarters,
                axisLabel: {
                    rotate: 0,
                    interval: function(index) {
                        // 判断数据长度是奇数还是偶数
                        const isOdd = processedData.quarters.length % 2 !== 0;

                        // 如果是奇数个标签，从第一个开始显示；如果是偶数个标签，从第二个开始显示
                        if (isOdd && index % 2 === 0 || !isOdd && index % 2 !== 0) {
                            return true; // 返回 true 表示显示该标签
                        }
                        return false; // 返回 false 表示不显示该标签
                    },
                    formatter: function(value) {
                        return value.substring(2); // 截取从第3个字符开始的字符串
                    }
                }
            },
            yAxis: [
                {
                    type: 'value',
                    name: '持仓占比',
                    position: 'left',
                    axisLabel: {
                        formatter: '{value} %'
                    },
                    splitNumber: 5, // 设置分割段数为5
                    min: 0, // 设置最小值
                    max: 100 // 设置最大值
                },
                {
                    type: 'value',
                    name: '股价(元)',
                    position: 'right',
                    show: false, // 默认不显示
                    axisLabel: {
                        formatter: '{value} 元'
                    },
                    splitNumber: 5, // 设置分割段数为5
                    min: priceMin, // 设置最小值
                    max: priceMax, // 设置最大值
                    // 如果需要更精细的控制，可以手动设置刻度
                    axisTick: {
                        alignWithLabel: true
                    },
                    axisLabel: {
                        formatter: function (value) {
                            // 可以根据需要调整刻度的显示格式
                            return value.toFixed(2);
                        }
                    }
                }
            ],
            series: [
                {
                    name: '持仓占比',
                    type: 'bar',
                    yAxisIndex: 0,
                    data: processedData.percent,
                    itemStyle: {
                        color: '#50A2E8'
                    },
                    barWidth: 18,
                    barGap: '24%'
                },
                {
                    name: '股价(元)',
                    type: 'line',
                    yAxisIndex: 1,
                    data: processedData.price,
                    smooth: false,
                    itemStyle: {
                        color: '#FB8C00'
                    },
                    lineStyle: {
                        width: 2,
                        type: 'solid'
                    }
                }
            ],
        };
    }, [getProcessedData]);

    const memoizedOption = useMemo(() => generateChartOption(), [generateChartOption]);

    useEffect(() => {
        if (chartRef.current) {
            chartRef.current.resize();
        }
    }, [memoizedOption]);

    const [columns, setColumns] = useState([]);
    const [dataSource, setDataSource] = useState([]);

    useEffect(() => {
        // 只有当有表格数据时才处理表格内容
        if (hasTableData && stockData.quarters) {
            const newColumns = [
                {
                    title: '季度',
                    dataIndex: 'indicator',
                    key: 'indicator',
                    fixed: 'left', // 固定第一列
                    render: (text) => (
                        <div className="first-column">{text}</div>
                    )
                }
            ];

            //倒转顺序
            const reversedQuarters = [...stockData.quarters].reverse();
            const reversedPrice = [...stockData.price].reverse();
            const reversedPercent = [...stockData.percent].reverse();
            const reversedHolderNum = [...stockData.holder_num].reverse();
            const reversedChangePercent = [...stockData.change_percent].reverse();

            reversedQuarters.forEach((quarter, index) => {
                newColumns.push({
                    title: quarter,
                    dataIndex: `quarter_${index}`,
                    key: `quarter_${index}`,
                    width: 100,
                    render: (text, record) => {
                        if (record.indicator === '持股数(股)') {
                            return <span>{unit.bigNumberTransform(text)
                            .replace(/(\d+\.\d+)千亿/, (match, p1) => {
                                return (parseFloat(p1) * 1000).toFixed(0) + '亿';
                            })
                            .replace(/(\d+\.\d+)千万/, (match, p1) => {
                                return (parseFloat(p1) * 1000).toFixed(0) + '万';
                            })} </span>;
                        }else if (record.indicator === '持仓占比') {
                            return <span>{text}%</span>;
                        }else if (record.indicator === '持仓变动') {
                            if (parseFloat(text) > 0 ) {
                                return <span style={{ color: 'red' }}>+{text}%</span>;
                            }else if (parseFloat(text) < 0 ) {
                                return <span style={{ color: 'green' }}>{text}%</span>;
                            }else {
                                return <span>{text}%</span>;
                            }
                        }
                    return text;
                    }
                });
            });

            setColumns(newColumns);

            const newDataSource = [];
            const rows = [
                { indicator: '变动时间', data: reversedQuarters },
                { indicator: '持仓占比', data: reversedPercent },
                { indicator: '持股数(股)', data: reversedHolderNum },
                { indicator: '持仓变动', data: reversedChangePercent },
                { indicator: '股价', data: reversedPrice },
            ];

            rows.forEach(row => {
                const rowObj = {
                    key: row.indicator,
                    indicator: row.indicator
                };

                row.data.forEach((value, index) => {
                    rowObj[`quarter_${index}`] = value;
                });
                newDataSource.push(rowObj);
            });
            setDataSource(newDataSource);
        } else {
            // 没有数据时清空表格内容
            setColumns([]);
            setDataSource([]);
        }
    }, [stockData, hasTableData]);

    const followFunc = (value) => {
        // 调用父组件传递的 onLogin 方法
        const path = isFollow ? '/pc/gudong/unfollow' : '/pc/gudong/follow';
        if (!unit.getToken()) {
            if (props.onLogin) {
                props.onLogin();
            }
            return false;
        }
        unit.httpGet(path, {"code": value, "type": 2, "apiversion": "8.9"}, (data) => {
            setIsFollow(!isFollow);
            if (isFollow) {
                // 已取消关注，去掉图标
                messageApi.open({
                    type: "Warning",
                    content: "已取消关注",
                    icon: null // 去掉图标
                });
            } else {
                // 已关注，保留默认图标
                messageApi.open({
                    type: "success",
                    content: "已关注"
                });
            }
        }, (code, msg) => {
            messageApi.open({
                type: "error",
                content: msg
            });
        });
        return false;
    };

    // 渲染标题容器
    const renderTitleContainer = () => {
        if (!stockInfo) return null;
    
        const { stock_name, stock_code, price, zde, zdf, is_follow } = stockInfo;
    
        return (
            <div className="title-container">
                <div className="left-section">
                    <div className="top-part">
                        <div className="stock-name" style={{
                            fontFamily: 'PingFang SC, PingFang SC',
                            fontWeight: 600,
                            fontSize: '20px',
                            color: '#333333',
                            lineHeight: '46px',
                            textAlign: 'left'
                        }}>
                            {stock_name || '未知股票'}
                        </div>
                        <div className="stock-code" style={{
                            fontFamily: 'PingFang SC',
                            fontWeight: 400,
                            fontSize: '16px',
                            color: '#999999',
                            lineHeight: '46px',
                            textAlign: 'left'
                        }}>
                            {stock_code || '未知代码'}
                        </div>
                    </div>
                    <div className="bottom-part">
                        <div className="price" style={{
                            fontFamily: 'Avenir Next',
                            fontWeight: 'bold',
                            fontSize: '28px',
                            color: parseFloat(zdf) >= 0 ? '#F23031' : '#29B427',  // 根据涨跌幅调整颜色
                            lineHeight: '46px',
                            textAlign: 'left'
                        }}>
                            {price || '--'}
                        </div>
                        <div className="zde-zdf" style={{
                            fontFamily: 'Avenir Next',
                            fontWeight: 'bold',
                            fontSize: '20px',
                            color: parseFloat(zdf) >= 0 ? '#F23031' : '#29B427',  // 根据涨跌幅调整颜色
                            lineHeight: '46px',
                            textAlign: 'left'
                        }}>
                            {zde ? `${parseFloat(zde).toFixed(2)} ${parseFloat(zdf) >= 0 ? '+' : ''}${parseFloat(zdf).toFixed(2)}%` : ''}
                        </div>
                    </div>
                </div>
                <div className="right-section">
                    {contextHolder}
                    {isFollow ? (
                        <div onClick={() => followFunc(code)} className="follow followed">
                            <span>已关注</span>
                        </div>
                    ) : (
                        <div onClick={() => followFunc(code)} className="follow unfollow">
                            <span> + 关注</span>
                        </div>
                    )}
                </div>          
            </div>
        );
    };

    if (renderTitleOnly) {
        return (
            <div className='content-block' style={{ width: '100%', height: '104px', borderRadius:'7px 7px 0px 0px',
                border: '1px solid #DDDDDD'}}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>{error}</div>
                ) : (
                    renderTitleContainer()
                )}
            </div>
        );
    } else if (renderChartAndTable) {
        const totalHeight = hasChartData || hasTableData ? '455px' : `50px`;
        return (
            <div className='content-block' style={{ width: '100%', height: totalHeight }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>{error}</div>
                ) : !hasChartData && !hasTableData ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>暂无图表数据</div>
                ) : (
                    <>
                        {hasChartData ? (
                            <ReactECharts
                                ref={chartRef}
                                option={memoizedOption}
                                style={{ height: '300px', width: '100%' }}
                            />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px' }}>暂无图表数据</div>
                        )}
                        
                        {hasTableData ? (
                            <Table
                                style={{ marginTop: '-50px' }}
                                size='small'
                                loading={false}
                                scroll={{ x: "max-content" }}
                                columns={columns}
                                dataSource={dataSource}
                                pagination={false}
                                className="custom-table"
                            />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px' }}>暂无表格数据</div>
                        )}
                    </>
                )}
            </div>
        );
    } else {
        return (
            <div className='content-block' style={{ width: '900px', height: '566px' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>加载中...</div>
                ) : error ? (
                    <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>{error}</div>
                ) : !hasChartData && !hasTableData ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>暂无数据</div>
                ) : (
                    <>
                        {renderTitleContainer()} {/* 渲染标题容器 */}
                        
                        {hasChartData ? (
                            <ReactECharts
                                ref={chartRef}
                                option={memoizedOption}
                                style={{ height: '300px', width: '100%' }}
                            />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px' }}>暂无图表数据</div>
                        )}
                        
                        {hasTableData ? (
                            <Table
                                size='small'
                                loading={false}
                                locale={{emptyText: "暂无数据" }} 
                                scroll={{ x: "max-content" }}
                                columns={columns}
                                dataSource={dataSource}
                                pagination={false}
                                className="custom-table"
                            />
                        ) : (
                            <div style={{ textAlign: 'center', padding: '20px' }}>暂无表格数据</div>
                        )}
                    </>
                )}
            </div>
        );
    }
};

export default StockChart;